import { useQuery } from '@tanstack/react-query';
import { getGiftData, giftContributionStatus } from '@/api/gifting/gifting';
import { useEffect, useState } from 'react';
import { miaConsoleLog } from '@/utils/mia-logger';
import {
  isTerminalOpenBankingStatus,
  PAYMENT_POLL_INTERVAL_MS,
  PAYMENT_POLL_WINDOW_MS,
} from '@/constants';

export function useGift(giftInviteCode: string) {
  // const filters = useAppStore(state => state.filters);
  return useQuery({
    queryKey: ['gift'],
    queryFn: () => getGiftData({ code: giftInviteCode }),
    // enabled: !!giftInviteCode,
  });
}

export type useOpenBankingPaymentStatusProps = {
  contributionId: string;
  code: string;
  paymentId: string;
  enabled?: boolean;
  /** Gap between polls, in milliseconds. */
  intervalMs?: number;
  /** How long to keep polling before giving up, in milliseconds. */
  windowMs?: number;
};

/**
 * Polls the contribution's payment status while TrueLayer settles it
 * (GIFTWEB-26 AC 3). Polling stops on a terminal status, on a request error,
 * or when the window runs out — the last of which raises `hasTimedOut` so the
 * callback can show the still-processing screen (AC 8).
 *
 * The window is wall-clock rather than a fixed attempt count: a slow API used
 * to eat the budget an attempt at a time, so "3 attempts" could mean anything
 * from six seconds to a minute.
 */
export function useOpenBankingPaymentStatus({
  contributionId,
  code,
  paymentId,
  enabled = true,
  intervalMs = PAYMENT_POLL_INTERVAL_MS,
  windowMs = PAYMENT_POLL_WINDOW_MS,
}: useOpenBankingPaymentStatusProps) {
  /** One polling session per payment; a new one starts its own window. */
  const pollKey = `${contributionId}:${paymentId}`;

  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [session, setSession] = useState(pollKey);

  // Adjusting state during render, rather than in an effect, so the flag is
  // already clear on the pass that renders the new payment.
  if (session !== pollKey) {
    setSession(pollKey);
    setHasTimedOut(false);
  }

  const isEnabled = !!paymentId && !!contributionId && !!code && enabled;

  const query = useQuery({
    queryKey: ['giftContributionStatus', contributionId, paymentId],
    queryFn: () => giftContributionStatus(contributionId, { code }),
    enabled: isEnabled,
    refetchInterval: ({ state }) => {
      if (hasTimedOut) return false;
      if (state.status === 'error') return false;
      if (isTerminalOpenBankingStatus(state.data?.data?.status)) return false;
      return intervalMs;
    },
    // A payment status is worth exactly as much as the poll that fetched it.
    staleTime: 0,
    gcTime: 0,
  });

  const status: string | undefined = query.data?.data?.status;
  const isSettled = isTerminalOpenBankingStatus(status) || query.isError;
  const isPolling = isEnabled && !isSettled && !hasTimedOut;

  useEffect(() => {
    if (!isPolling) return;

    const timer = setTimeout(() => {
      setHasTimedOut(true);
      console.warn(`Payment status polling stopped after ${windowMs}ms`);
      miaConsoleLog('warn', {
        event: 'payment_status_polling_window_elapsed',
        windowMs,
        contributionId,
        paymentId,
      });
    }, windowMs);

    return () => clearTimeout(timer);
  }, [isPolling, windowMs, pollKey, contributionId, paymentId]);

  return { ...query, status, isPolling, hasTimedOut };
}
